/**
 * RouteServicesPanel v2 — Icons on map + hover InfoWindow
 *
 * Flow:
 * 1. Toggle button (arrow icon only) in bottom-right
 * 2. Click → shows category chips bar
 * 3. Select category → searches Google Places along route → shows icons on map
 * 4. Hover on icon → InfoWindow with photo, rating, status, distance
 * 5. Selecting a category hides sidebar panels for clean view
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Box, Chip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectUI } from '../../store/slices/uiSlice';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import HotelIcon from '@material-ui/icons/Hotel';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import LocalParkingIcon from '@material-ui/icons/LocalParking';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';

const CATEGORIES = [
  { key: 'fuel', label: 'Combustível', labelEn: 'Fuel', icon: LocalGasStationIcon, color: '#ef4444', type: 'gas_station' },
  { key: 'hotel', label: 'Hotéis', labelEn: 'Hotels', icon: HotelIcon, color: '#8b5cf6', type: 'lodging' },
  { key: 'restaurant', label: 'Restaurantes', labelEn: 'Restaurants', icon: RestaurantIcon, color: '#f59e0b', type: 'restaurant' },
  { key: 'rest', label: 'Descanso', labelEn: 'Rest', icon: LocalParkingIcon, color: '#22c55e', type: 'parking' },
];

const SEARCH_INTERVAL_KM = 50;
const SEARCH_RADIUS = 5000;

const useStyles = makeStyles(() => ({
  toggleBtn: {
    position: 'absolute',
    bottom: 24,
    right: 60,
    zIndex: 190,
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.2)', background: '#f8fafc' },
  },
  categoryBar: {
    position: 'absolute',
    bottom: 24,
    right: 108,
    zIndex: 190,
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(8px)',
    borderRadius: 24,
    padding: '6px 12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
  },
  chip: {
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontSize: '0.6rem !important',
    fontWeight: '700 !important',
    height: '26px !important',
    borderRadius: '8px !important',
  },
}));

function getSearchPoints(coordinates, intervalKm) {
  if (!coordinates || coordinates.length < 2) return [];
  const points = [];
  let accDist = 0;
  let lastSearchDist = -intervalKm;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lng1, lat1] = coordinates[i];
    const [lng2, lat2] = coordinates[i + 1];
    const dx = (lng2 - lng1) * 111 * Math.cos(lat1 * Math.PI / 180);
    const dy = (lat2 - lat1) * 111;
    accDist += Math.sqrt(dx * dx + dy * dy);

    if (accDist - lastSearchDist >= intervalKm) {
      points.push({ lat: lat2, lng: lng2, km: Math.round(accDist) });
      lastSearchDist = accDist;
    }
  }
  return points;
}

function RouteServicesPanel({ mapRef, onServicesLoaded }) {
  const classes = useStyles();
  const route = useSelector(selectCurrentRoute);
  const { lang } = useSelector(selectUI);
  const isPt = lang === 'pt';

  const [showCategories, setShowCategories] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState({}); // { fuel: [...], hotel: [...] }

  const cr = route.computedRoute;
  const hasRoute = !!cr?.geometry?.coordinates;

  // Search services for a category
  const searchCategory = useCallback(async (catKey) => {
    if (!mapRef?.current || !hasRoute || cache[catKey]) {
      if (cache[catKey] && onServicesLoaded) onServicesLoaded(cache[catKey]);
      return;
    }

    const map = mapRef.current;
    const coords = cr.geometry.coordinates;
    const searchPoints = getSearchPoints(coords, SEARCH_INTERVAL_KM);
    const cat = CATEGORIES.find(c => c.key === catKey);
    if (!cat || searchPoints.length === 0) return;

    setLoading(true);

    try {
      const service = new window.google.maps.places.PlacesService(map);
      const allResults = [];
      const seen = new Set();

      for (const point of searchPoints) {
        try {
          const results = await new Promise((resolve) => {
            service.nearbySearch({
              location: { lat: point.lat, lng: point.lng },
              radius: SEARCH_RADIUS,
              type: cat.type,
            }, (res, status) => {
              if (status === 'OK' && res) resolve(res);
              else resolve([]);
            });
          });

          for (const place of results.slice(0, 3)) {
            if (seen.has(place.place_id)) continue;
            seen.add(place.place_id);

            const photoUrl = place.photos?.[0]?.getUrl({ maxWidth: 300, maxHeight: 150 });
            allResults.push({
              id: place.place_id,
              name: place.name,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              rating: place.rating,
              totalRatings: place.user_ratings_total,
              photo: photoUrl || null,
              isOpen: place.opening_hours?.isOpen?.() ?? null,
              address: place.vicinity,
              km: point.km,
              category: catKey,
              color: cat.color,
            });
          }
        } catch { /* skip */ }
      }

      allResults.sort((a, b) => a.km - b.km);
      setCache(prev => ({ ...prev, [catKey]: allResults }));
      if (onServicesLoaded) onServicesLoaded(allResults);
    } catch (err) {
      console.warn('Places search failed:', err);
    }

    setLoading(false);
  }, [mapRef, hasRoute, cr, cache, onServicesLoaded]);

  // When category changes, search and update markers
  useEffect(() => {
    if (activeCategory) {
      searchCategory(activeCategory);
    }
  }, [activeCategory, searchCategory]);

  // Reset cache when route changes
  useEffect(() => {
    setCache({});
    setActiveCategory(null);
    setShowCategories(false);
    if (onServicesLoaded) onServicesLoaded([]);
  }, [cr?.geometry]); // eslint-disable-line

  // Toggle categories bar
  function handleToggle() {
    if (showCategories) {
      setShowCategories(false);
      setActiveCategory(null);
      if (onServicesLoaded) onServicesLoaded([]);
    } else {
      setShowCategories(true);
    }
  }

  function handleSelectCategory(catKey) {
    if (activeCategory === catKey) {
      // Deselect
      setActiveCategory(null);
      if (onServicesLoaded) onServicesLoaded([]);
    } else {
      setActiveCategory(catKey);
    }
  }

  if (!hasRoute) return null;

  return (
    <>
      {/* Toggle button — round, bottom right */}
      <Box className={classes.toggleBtn} onClick={handleToggle}>
        {showCategories
          ? <KeyboardArrowDownIcon style={{ fontSize: 22, color: '#3b82f6' }} />
          : <KeyboardArrowUpIcon style={{ fontSize: 22, color: '#3b82f6' }} />
        }
      </Box>

      {/* Category chips bar */}
      {showCategories && (
        <Box className={classes.categoryBar}>
          {loading && <CircularProgress size={16} style={{ color: '#3b82f6' }} />}
          {CATEGORIES.map(cat => {
            const CatIcon = cat.icon;
            const isActive = activeCategory === cat.key;
            const count = cache[cat.key]?.length || 0;
            return (
              <Chip
                key={cat.key}
                icon={<CatIcon style={{ fontSize: 14, color: isActive ? '#fff' : cat.color }} />}
                label={`${isPt ? cat.label : cat.labelEn}${count > 0 ? ` (${count})` : ''}`}
                className={classes.chip}
                onClick={() => handleSelectCategory(cat.key)}
                style={{
                  background: isActive ? cat.color : '#fff',
                  color: isActive ? '#fff' : '#475569',
                  border: `1.5px solid ${isActive ? cat.color : '#e2e8f0'}`,
                }}
              />
            );
          })}
          <Box onClick={handleToggle} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginLeft: 2 }}>
            <CloseIcon style={{ fontSize: 16, color: '#94a3b8' }} />
          </Box>
        </Box>
      )}
    </>
  );
}

export default RouteServicesPanel;
